import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBooking } from "../../../../context/BookingContext";
import { getLiveGrips } from "../../../../api/api";
import { getDubaiDate } from "../../../../utils/date";
import styles from "./GripBookingForm.module.css";
import useAdminGuard from "../../../../hooks/useAuthGuard";

const REG_FEE = 20;
const EXP_FEE = 30;

const GripBookingForm = () => {
  useAdminGuard();
  const [grips, setGrips] = useState([]);
  const { pickupService, addBooking, bookings } = useBooking();
  const [validationError, setValidationError] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  const {
    customerId,
    customerName = "N/A",
    freeServices = 0,
    usedServices = 0,
    freeServicesTotal = 0,
    invoiceNo,
    instoreLocation = null,
    gripTypeConstraint = null,
    isAddMore = false,
    racketName: prefilledRacketName,
    bookingDate: prefilledDate,
    fromFinal = false,
    returnPath,
    previousBookings = [],
    pickupService: statePickupService = null,
  } = location.state || {};

  // Use pickup service from location state if available (for add more scenarios), otherwise from context
  const effectivePickupService = statePickupService || pickupService;

  const today = () => new Date().toISOString().split("T")[0];
  
  const effectiveConstraint = gripTypeConstraint;
  const comingFromFinal = Boolean(fromFinal || returnPath);

  const [racketName, setRacketName] = useState(prefilledRacketName || "");
  const [selectedGripId, setSelectedGripId] = useState(null);
  const [selectedGripType, setSelectedGripType] = useState("");
  const [bookingDate, setBookingDate] = useState(prefilledDate || today());
  const [totalCost, setTotalCost] = useState("");
  const [showExpressCutoffPopup, setShowExpressCutoffPopup] = useState(false);
  const [showNoDatePopup, setShowNoDatePopup] = useState(false);

  const dateChosen = !!bookingDate;
  const isGripSelected = Boolean(selectedGripId);

  const EXPRESS_CUTOFF_HOUR = 17;
  const afterExpressCutoff = (ymd) => {
    const dubai = getDubaiDate();
    return (ymd === dubai.ymd || !ymd) && dubai.hour >= EXPRESS_CUTOFF_HOUR;
  };

  useEffect(() => {
    if (!selectedGripId || !selectedGripType) {
      setTotalCost("");
      return;
    }

    const selected = grips.find((g) => g.grip_id === selectedGripId);
    if (!selected) {
      setTotalCost("");
      return;
    }

    const regular = grips.find(
      (g) => g.name === selected.name && g.type === "Regular"
    );

    const gripCost = Number(regular?.cost ?? selected?.cost ?? 0);
    const fee = selectedGripType === "Express" ? EXP_FEE : REG_FEE;
    setTotalCost(gripCost + fee);
  }, [selectedGripId, selectedGripType, grips]);

  useEffect(() => {
    if (!showNoDatePopup) return;
    const t = setTimeout(() => setShowNoDatePopup(false), 2200);
    return () => clearTimeout(t);
  }, [showNoDatePopup]);

  const handleNextClick = () => {
    if (!bookingDate.trim()) {
      setValidationError("Please select a booking date.");
      return;
    }
    if (!selectedGripId || !selectedGripType) {
      setValidationError("Please select a grip and type before proceeding.");
      return;
    }
    if (!racketName.trim()) {
      setValidationError("Please enter a racket name.");
      return;
    }

    setValidationError("");
    const selected = grips.find((g) => g.grip_id === selectedGripId);
    if (!selected?.bc_sku && !selected?.sku) {
      setValidationError(
        "Selected grip is missing a SKU. Please pick another grip."
      );
      return;
    }

    const newBooking = {
      customerId,
      invoiceNo,
      racketName,
      bookingDate,
      serviceKind: "grip",
      gripId: selectedGripId,
      gripType: selectedGripType,
      totalCost,
      gripName: selected?.name || "",
      gripCost: Number(selected?.cost ?? 0),
      serviceFee: selectedGripType === "Express" ? EXP_FEE : REG_FEE,
      bc_sku: selected?.bc_sku ?? selected?.sku ?? null,
      bc_name: selected?.bc_name ?? selected?.name ?? null,
      bc_brand: selected?.bc_brand ?? null,
      bc_price_inc_tax: Number(
        selected?.bc_price_inc_tax ?? selected?.cost ?? 0
      ),
      bc_image_url: selected?.image ?? null,
    };

    addBooking(newBooking);

    if (fromFinal) {
      navigate(returnPath || "/dashboard/grip-booking-final", {
        state: {
          customerId,
          customerName,
          bookingDate,
          pickupService: effectivePickupService,
          instoreLocation,
          services: "Grip Service",
          previousBookings: [...previousBookings, newBooking],
        },
        replace: true,
      });
      return;
    }

    navigate("/dashboard/grip-booking-details", {
      state: {
        customerId,
        customerName,
        bookingDate,
        freeServices,
        usedServices,
        freeServicesTotal,
        pickupService: effectivePickupService,
        services: "Grip Service",
        grip_id: selectedGripId,
        grip_type: selectedGripType,
        instoreLocation,
        racketName,
        lockServiceSelection: isAddMore === true,
        serviceKind: "grip",
      },
    });
  };

  useEffect(() => {
    (async () => {
      try {
        let items = await getLiveGrips();
        items = Array.isArray(items)
          ? items
          : Array.isArray(items?.data)
          ? items.data
          : [];

        const normalizeType = (t) => {
          if (!t) return "Regular";
          const s = String(t).toLowerCase();
          return s === "express" ? "Express" : "Regular";
        };

        const normalized = items.map((x) => ({
          ...x,
          type: normalizeType(x.type ?? x.service_type),
          basePrice: Number(x.basePrice ?? x.cost_per_unit ?? REG_FEE),
          stockStatus: x.stockStatus ?? x.stock_status ?? "available",
          qty: x.qty ?? x.remaining_quantity ?? 999,
          image:
            x.image ||
            x.image_url ||
            x.thumbnail_url ||
            "/images/grip-placeholder.png",
          sku: x.sku ?? x.product_code ?? null,
          bc_sku: x.bc_sku ?? x.sku ?? x.product_code ?? null,
          bc_name: x.bc_name ?? x.name ?? null,
          bc_brand: x.bc_brand ?? x.brand ?? null,
          bc_price_inc_tax: Number(
            x.bc_price_inc_tax ?? x.cost ?? x.basePrice ?? 0
          ),
          cost: Number(x.cost ?? x.bc_price_inc_tax ?? 0),
        }));

        // BYOG fallback (grip cost = 0)
        const BYOG_NAME_LOCAL = "Bring Own Grip";
        const hasBYOG = normalized.some((g) => g.name === BYOG_NAME_LOCAL);
        const withBYOG = hasBYOG
          ? normalized
          : [
              ...normalized,
              {
                grip_id: 900000001,
                name: BYOG_NAME_LOCAL,
                type: "Regular",
                basePrice: REG_FEE,
                priceDifference: 0,
                stockStatus: "available",
                qty: 999,
                sku: "BYOG-REG",
                bc_sku: "BYOG-REG",
                bc_name: BYOG_NAME_LOCAL,
                bc_price_inc_tax: 0,
                image: "/images/grip-placeholder.png",
                cost: 0,
              },
              {
                grip_id: 900000002,
                name: BYOG_NAME_LOCAL,
                type: "Express",
                basePrice: REG_FEE,
                priceDifference: EXP_FEE - REG_FEE,
                stockStatus: "available",
                qty: 999,
                sku: "BYOG-EXP",
                bc_sku: "BYOG-EXP",
                bc_name: BYOG_NAME_LOCAL,
                bc_price_inc_tax: 0,
                image: "/images/grip-placeholder.png",
                cost: 0,
              },
            ];

        setGrips(withBYOG);
      } catch (err) {
        console.error("Error fetching grips:", err);
        setGrips([]);
      }
    })();
  }, []);

  const BYOG_NAME = "Bring Own Grip";

  const scrollToBYOG = () => {
    const byogRegular = grips.find(
      (g) => g.name === BYOG_NAME && g.type === "Regular"
    );

    if (byogRegular) {
      setSelectedGripId(byogRegular.grip_id);
      setSelectedGripType("Regular");
      const gripCost = Number(byogRegular.cost ?? 0);
      setTotalCost(gripCost + REG_FEE);
    }

    const target = document.getElementById("byog-card");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Create Booking</h2>

      {validationError && (
        <p className={styles.validationError}>{validationError}</p>
      )}

      <div className={styles.headerRow}>
        <div className={styles.infoChip}>
          <span className={styles.chipLabel}>Customer</span>
          <span className={styles.chipValue} title={customerName}>
            {customerName}
          </span>
        </div>

        <div className={styles.infoChip}>
          <span className={styles.chipLabel}>Service</span>
          <span className={styles.chipValue}>Grip Service</span>
        </div>

        <div
          className={styles.turnaroundCard}
          role="note"
          aria-label="Turnaround times"
        >
          <div className={styles.taHead}>
            <span className={styles.taDot} aria-hidden="true"></span>
            <strong>Turnaround</strong>
          </div>
          <ul className={styles.taList}>
            <li>
              <strong>Regular:</strong> Next day
            </li>
            <li>
              <strong>Express:</strong> Within 6 hours
            </li>
          </ul>
        </div>
      </div>

      <div className={styles.formGroup}>
        <div className={styles.inlineRow}>
          <div className={styles.field}>
            <label className={styles.label}>Racket Name:</label>
            <input
              type="text"
              className={styles.inputField}
              placeholder="Enter racket name"
              value={racketName}
              onChange={(e) => setRacketName(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Booking Date:</label>
            <input
              type="date"
              value={bookingDate}
              onChange={(e) => {
                if (!comingFromFinal && !isAddMore) setBookingDate(e.target.value);
              }}
              className={styles.dateInput}
              min={comingFromFinal || isAddMore ? undefined : today()}
              disabled={comingFromFinal || isAddMore}
            />
            {!dateChosen && (
              <div className={styles.inlineHint}>
                Please select a booking date.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.formGroup}>
        <div className={styles.labelRow}>
          <div className={styles.labelTitle}>
            <label className={styles.label}>
              Grip Options{" "}
              {effectiveConstraint
                ? `(Type Locked: ${effectiveConstraint})`
                : ""}
            </label>

            <div className={styles.infoWrap} aria-live="polite">
              <button
                type="button"
                className={styles.infoBtn}
                aria-label="What is a base (replacement) grip vs an overgrip?"
              >
                i
              </button>
              <div className={styles.infoTooltip} role="tooltip">
                <strong>Base (Replacement) Grip</strong>: main grip layer on the
                handle — thicker & cushioned, replaces the worn original.
                <br />
                <strong>Overgrip</strong>: thin wrap over the base grip for
                extra tack or absorbency — cheaper, changed more often.
              </div>
            </div>
          </div>

          <button
            type="button"
            className={styles.byosInlineBtn}
            onClick={scrollToBYOG}
          >
            Want to bring your own grip? Click here
          </button>
        </div>
        <div className={styles.cardsGrid}>
          {(() => {
            const regulars = grips.filter((g) => g.type === "Regular");

            const sortedRegulars = regulars.slice().sort((a, b) => {
              const aOOS =
                a.stockStatus === "not_in_stock" || Number(a.qty) === 0;
              const bOOS =
                b.stockStatus === "not_in_stock" || Number(b.qty) === 0;

              if (aOOS !== bOOS) return aOOS ? 1 : -1;
              return (a.name || "").localeCompare(b.name || "");
            });

            return sortedRegulars.map((regular) => {
              const express = grips.find(
                (x) => x.name === regular.name && x.type === "Express"
              );

              if (effectiveConstraint === "Express" && !express) return null;

              const pickType =
                effectiveConstraint || selectedGripType || "Regular";
              const idForCard =
                (effectiveConstraint || pickType) === "Express"
                  ? express?.grip_id
                  : regular.grip_id;

              const gripCost = Number(regular?.cost ?? 0);
              const previewFee =
                (effectiveConstraint || pickType) === "Express"
                  ? EXP_FEE
                  : REG_FEE;
              const previewTotal = gripCost + previewFee;

              const isSelected = selectedGripId === idForCard;
              const isBYOG = regular.name === BYOG_NAME;
              const isOutOfStock =
                regular.stockStatus === "not_in_stock" ||
                Number(regular.qty) === 0;

              return (
                <div
                  key={regular.grip_id}
                  className={`${styles.card} ${
                    isSelected ? styles.cardSelected : ""
                  } ${isOutOfStock ? styles.cardDisabled : ""}`}
                  id={isBYOG ? "byog-card" : undefined}
                >
                  <label className={styles.cardTop}>
                    <input
                      type="radio"
                      name="selectedGrip"
                      value={idForCard || ""}
                      onChange={() => {
                        if (isOutOfStock) return;
                        const forcedType =
                          effectiveConstraint || pickType || "Regular";
                        setSelectedGripId(idForCard);
                        setSelectedGripType(forcedType);
                        const fee =
                          forcedType === "Express" ? EXP_FEE : REG_FEE;
                        setTotalCost(gripCost + fee);
                      }}
                      checked={isSelected}
                      disabled={!idForCard || isOutOfStock}
                      className={styles.cardSelector}
                    />

                    <div className={styles.imageWrap}>
                      <img
                        className={styles.cardImage}
                        src={
                          regular.image ||
                          express?.image ||
                          "/images/grip-placeholder.png"
                        }
                        alt={regular.name}
                        loading="lazy"
                      />

                      <div className={styles.imageBadges}>
                        {isOutOfStock && (
                          <span className={styles.oosBadge}>Out of Stock</span>
                        )}
                      </div>
                    </div>
                  </label>

                  <div className={styles.cardBody}>
                    <div className={styles.cardTitle} title={regular.name}>
                      {regular.name}
                    </div>

                    {effectiveConstraint ? (
                      <div className={styles.lockedBadge}>
                        {effectiveConstraint}
                      </div>
                    ) : (
                      <div className={styles.segmented}>
                        <label className={styles.radioLabel}>
                          <input
                            type="radio"
                            name={`gripType-${regular.grip_id}`}
                            value="Regular"
                            onChange={() => {
                              if (isOutOfStock) return;
                              setSelectedGripId(regular.grip_id);
                              setSelectedGripType("Regular");
                              setTotalCost(gripCost + REG_FEE);
                            }}
                            checked={
                              isSelected && selectedGripType === "Regular"
                            }
                            disabled={
                              isOutOfStock ||
                              (selectedGripId !== regular.grip_id &&
                                selectedGripId !== express?.grip_id)
                            }
                          />
                          <span>Regular (+ {REG_FEE} service)</span>
                        </label>

                        {express && (
                          <label
                            className={styles.radioLabel}
                            onClick={(e) => {
                              if (isOutOfStock) {
                                e.preventDefault();
                                return;
                              }
                              if (!dateChosen) {
                                e.preventDefault();
                                setShowNoDatePopup(true);
                                return;
                              }
                              if (afterExpressCutoff(bookingDate)) {
                                e.preventDefault();
                                setShowExpressCutoffPopup(true);
                                return;
                              }
                              setSelectedGripId(express.grip_id);
                              setSelectedGripType("Express");
                              setTotalCost(gripCost + EXP_FEE);
                            }}
                          >
                            <input
                              type="radio"
                              name={`gripType-${regular.grip_id}`}
                              value="Express"
                              onChange={() => {}}
                              checked={
                                isSelected && selectedGripType === "Express"
                              }
                              readOnly
                              disabled={
                                isOutOfStock ||
                                !dateChosen ||
                                (selectedGripId !== regular.grip_id &&
                                  selectedGripId !== express?.grip_id) ||
                                afterExpressCutoff(bookingDate)
                              }
                            />
                            <span>Express (+ {EXP_FEE} service)</span>
                          </label>
                        )}
                      </div>
                    )}

                    <div className={styles.cardFooter}>
                      <div className={styles.priceLabel}>Cost</div>
                      <div className={styles.priceStack}>
                        <div className={styles.priceRow}>
                          <span>Grip</span>
                          <span>
                            <span className="dirham-symbol">&#xea;</span>{" "}
                            {Number(gripCost).toFixed(2)}
                          </span>
                        </div>
                        <div className={styles.priceRow}>
                          <span>Service</span>
                          <span>
                            + <span className="dirham-symbol">&#xea;</span>{" "}
                            {((
                              isSelected
                                ? selectedGripType === "Express"
                                : (effectiveConstraint || pickType) ===
                                  "Express"
                            )
                              ? EXP_FEE
                              : REG_FEE
                            ).toFixed(2)}
                          </span>
                        </div>
                        <div className={styles.priceRowTotal}>
                          <span>Total</span>
                          <span>
                            <span className="dirham-symbol">&#xea;</span>{" "}
                            {isSelected
                              ? Number(totalCost).toFixed(2)
                              : Number(previewTotal).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>

      {isGripSelected && (
        <div className={`${styles.buttonContainer} ${styles.fixedBottom}`}>
          <button className={styles.nextButton} onClick={handleNextClick}>
            Next
          </button>
          <button
            className={styles.cancelButton}
            onClick={() => {
              setSelectedGripId(null);
              setSelectedGripType("");
              setTotalCost("");
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {showExpressCutoffPopup && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>Express Service Cutoff</h3>
            <p>
              Express service cutoff reached. Please choose Regular or call
              store:<strong> +971-555-123456</strong>
            </p>
            <button
              onClick={() => setShowExpressCutoffPopup(false)}
              className={styles.closeModalButton}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {showNoDatePopup && (
        <div className={styles.toast} role="status" aria-live="polite">
          Please choose a booking date before selecting Express.
        </div>
      )}
    </div>
  );
};

export default GripBookingForm;
